import React, { useState, useEffect } from "react";
import { TodoProvider, useTodos } from "./TodoContext";
import { motion, AnimatePresence } from "framer-motion";
import "./index.css";

const ThemeToggle = () => {
  const [isDark, setIsDark] = useState(() => {
    // Cek preferensi sistem atau localStorage
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme) return savedTheme === "dark";
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  useEffect(() => {
    // Terapkan kelas dark berdasarkan state
    if (isDark) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [isDark]);

  const toggleTheme = () => setIsDark((prev) => !prev);

  return (
    <motion.button
      onClick={toggleTheme}
      className="p-2 bg-gray-200 dark:bg-gray-700 rounded-full hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}>
      {isDark ? "☀️" : "🌙"}
    </motion.button>
  );
};

const TodoList = () => {
  const { todos, toggleTodo, deleteTodo, editTodo } = useTodos();
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  const startEdit = (id, text) => {
    setEditingId(id);
    setEditText(text);
  };

  const saveEdit = (id) => {
    if (editText.trim()) {
      editTodo(id, editText);
      setEditingId(null);
    }
  };

  return (
    <ul className="space-y-4">
      <AnimatePresence>
        {todos.map((todo) => (
          <motion.li
            key={todo.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
            className={`flex justify-between items-center p-4 backdrop-blur-md bg-white/30 dark:bg-gray-800/30 rounded-xl shadow-md hover:shadow-lg transition-shadow ${
              todo.done ? "line-through text-gray-500 dark:text-gray-400" : ""
            }`}>
            {editingId === todo.id ? (
              <>
                <input
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  className="flex-grow bg-transparent border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 text-gray-900 dark:text-gray-100"
                  aria-label="Edit todo"
                />
                <motion.button
                  onClick={() => saveEdit(todo.id)}
                  className="ml-2 text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                  aria-label="Save edit"
                  whileHover={{ scale: 1.2 }}>
                  💾
                </motion.button>
              </>
            ) : (
              <>
                <span className="flex-grow  hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-gray-900 dark:text-gray-100">
                  {todo.done ? "✅ " : "⏳ "}
                  {todo.text}
                </span>
                <motion.button
                  onClick={() => toggleTodo(todo.id)}
                  className="mx-2 text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                  aria-label="Toggle done"
                  whileHover={{ scale: 1.2 }}>
                  🔄
                </motion.button>
                <motion.button
                  onClick={() => startEdit(todo.id, todo.text)}
                  className="mx-2 text-yellow-500 hover:text-yellow-700 dark:text-yellow-400 dark:hover:text-yellow-300"
                  aria-label="Edit todo"
                  whileHover={{ scale: 1.2 }}>
                  ✏️
                </motion.button>
                <motion.button
                  onClick={() => deleteTodo(todo.id)}
                  className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                  aria-label="Delete todo"
                  whileHover={{ scale: 1.2 }}>
                  🗑️
                </motion.button>
              </>
            )}
          </motion.li>
        ))}
      </AnimatePresence>
    </ul>
  );
};

const AddTodo = () => {
  const { addTodo } = useTodos();
  const [text, setText] = useState("");

  const handleAdd = () => {
    if (text.trim()) {
      addTodo(text);
      setText("");
    }
  };

  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="flex mt-4">
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyPress={(e) => e.key === "Enter" && handleAdd()}
        placeholder="Add new todo 📝"
        className="flex-grow p-3 bg-white/50 dark:bg-gray-800/50 rounded-l-xl border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 text-gray-900 dark:text-gray-100"
        aria-label="New todo input"
      />
      <motion.button
        onClick={handleAdd}
        className="p-3 bg-blue-500 text-white rounded-r-xl hover:bg-blue-600 dark:hover:bg-blue-500 transition-colors"
        aria-label="Add todo"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}>
        ➕
      </motion.button>
    </motion.div>
  );
};

const Filters = () => {
  const { setFilter } = useTodos();
  const filters = ["all", "active", "completed"];

  return (
    <div className="flex space-x-2 mt-4 mb-4">
      {filters.map((filter) => (
        <motion.button
          key={filter}
          onClick={() => setFilter(filter)}
          className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-gray-900 dark:text-gray-100"
          whileHover={{ scale: 1.05 }}>
          {filter.charAt(0).toUpperCase() + filter.slice(1)}
        </motion.button>
      ))}
    </div>
  );
};

const App = () => {
  return (
    <TodoProvider>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-50 dark:from-gray-900 dark:to-blue-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md backdrop-blur-lg bg-white/20 dark:bg-gray-900/20 p-6 rounded-xl shadow-xl">
          <div className="flex justify-between items-center mb-6">
            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="text-3xl font-bold text-blue-600 dark:text-blue-400 flex items-center">
              Todo App 🚀
            </motion.h1>
            <ThemeToggle />
          </div>
          <AddTodo />
          <Filters />
          <TodoList />
        </div>
      </div>
    </TodoProvider>
  );
};

export default App;
